import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Heading,
  Link,
  Paragraph,
  Skeleton,
  Tag,
} from '@digdir/designsystemet-react';
import { useFeatureFlag } from '../../../contexts/FeatureFlagContext';
import {
  useHmsRiskMatrixQuery,
  useHmsTemplateQuery,
} from '../../../hooks/queries/hms/useHmsQueries';
import { useGetHrPsychologicalSafetyOverviewQuery } from '../../../hooks/queries/hr/useGetHrPsychologicalSafetyOverviewQuery';
import { useHrHomeQuery } from '../../../hooks/queries/hr/useHrHomeQuery';
import { useHrWorkEnvironmentOverviewQuery } from '../../../hooks/queries/hr/useHrWorkEnvironmentOverviewQuery';
import { useMeasureDeadlinesQuery } from '../../../hooks/queries/hr/useMeasureDeadlinesQuery';
import { questionLanguageFor } from '../../FormSubmission/workEnvironmentFormView';
import {
  followUpCards,
  HomeCard,
  HomeData,
  moduleCards,
  nextDeadlines,
} from './homeView';
import { useUserOnboardingQuery } from '../../../hooks/queries/onboarding/useUserOnboarding';
import { GettingStartedPanel } from '../../GettingStarted/GettingStartedPanel';
import classes from './HRHome.module.css';

const Cards = ({
  id,
  title,
  cards,
}: {
  id: string;
  title: string;
  cards: HomeCard[];
}): ReactElement => {
  const { t } = useTranslation();
  return (
    <section aria-labelledby={id} className={classes.section}>
      <Heading level={2} data-size="md" id={id}>
        {title}
      </Heading>
      <ul className={classes.cards}>
        {cards.map((card) => (
          <li key={card.key} className={classes.card}>
            <div className={classes.cardTop}>
              <span className={`ds-paragraph ${classes.subtle}`} data-size="sm">
                {t(`hrHome.modules.${card.module}.name`)}
              </span>
              <Tag data-size="sm" data-color={card.color}>
                {card.tag}
              </Tag>
            </div>
            <span className={`ds-heading ${classes.value}`} data-size="xl">
              {card.value}
            </span>
            <div className={classes.cardText}>
              <Heading level={3} data-size="2xs">
                {card.title}
              </Heading>
              <Paragraph data-size="sm" className={classes.subtle}>
                {card.text}
              </Paragraph>
            </div>
            {card.link && (
              <Link asChild data-size="sm" className={classes.start}>
                <RouterLink to={card.link.to}>{card.link.text}</RouterLink>
              </Link>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
};

const Deadlines = ({ data }: { data: HomeData }): ReactElement => {
  const { t, i18n } = useTranslation();
  const deadlines = nextDeadlines(data, t, i18n.language);
  return (
    <section aria-labelledby="home-deadlines" className={classes.deadlines}>
      <Heading level={2} data-size="sm" id="home-deadlines">
        {t('hrHome.deadlines.title')}
      </Heading>
      {deadlines.length === 0 ? (
        <Paragraph data-size="sm">{t('hrHome.deadlines.none')}</Paragraph>
      ) : (
        <ol className={classes.deadlineList}>
          {deadlines.map((deadline) => (
            <li key={deadline.key}>
              <time dateTime={deadline.at} className="ds-label" data-size="sm">
                {deadline.date}
              </time>
              <span className="ds-paragraph" data-size="sm">
                {deadline.text}{' '}
                <span className={classes.subtle}>
                  {t('hrHome.deadlines.module', {
                    module: deadline.moduleName,
                  })}
                </span>
              </span>
            </li>
          ))}
        </ol>
      )}
    </section>
  );
};

/**
 * HR's home page: the most important across the modules, each summed up in a card that links
 * to the module. Only modules the organisation uses are shown.
 */
export const HRHome = (): ReactElement => {
  const { t, i18n } = useTranslation();
  const { data: onboarding } = useUserOnboardingQuery();
  // «Kom i gang» lives here for the goal of mapping the working environment
  const gettingStarted =
    onboarding?.gettingStarted && onboarding.goal === 'WORK_ENVIRONMENT' ? onboarding : null;
  const beforeFirstStep = Boolean(gettingStarted && !gettingStarted.firstStepDone);
  const { hasFeature } = useFeatureFlag();
  const withWork = hasFeature('WORK_ENVIRONMENT');
  const withPulse = hasFeature('CUSTOM_SURVEYS');
  const language = questionLanguageFor(i18n.language);
  const overview = useHrWorkEnvironmentOverviewQuery(language, withWork);
  const measures = useMeasureDeadlinesQuery(language, withWork);
  const riskMatrix = useHmsRiskMatrixQuery(withWork);
  const template = useHmsTemplateQuery(withWork);
  const safety = useGetHrPsychologicalSafetyOverviewQuery();
  const home = useHrHomeQuery();
  const queries = [overview, measures, riskMatrix, template, safety, home];
  const isLoading = queries.some((query) => query.isLoading);
  const isError = queries.some((query) => query.isError);

  const work =
    overview.data && measures.data && riskMatrix.data && template.data
      ? {
          overview: overview.data,
          measures: measures.data,
          riskMatrix: riskMatrix.data,
          template: template.data,
        }
      : null;
  const data: HomeData | null =
    safety.data && home.data && (!withWork || work)
      ? {
          workEnvironment: withWork ? work : null,
          psychologicalSafety: {
            teams: safety.data.teams,
            open: home.data.psychologicalSafety.open,
          },
          pulse: withPulse ? home.data.pulse : null,
        }
      : null;
  const followUp = data ? followUpCards(data, t, i18n.language) : [];

  return (
    <div className={classes.page} data-size="md">
      <header className={classes.hero}>
        {overview.data && (
          <span className={`ds-label ${classes.eyebrow}`} data-size="sm">
            {t('hrOverview.eyebrow', {
              organization: overview.data.organizationName,
            })}
          </span>
        )}
        <Heading level={1} data-size="xl">
          {gettingStarted?.firstName ? t('gettingStarted.home.welcome', { name: gettingStarted.firstName }) : t('hrHome.title')}
        </Heading>
        <Paragraph data-size="lg">
          {gettingStarted
            ? t(beforeFirstStep ? 'gettingStarted.home.lead' : 'gettingStarted.home.leadDone')
            : t('hrHome.intro')}
        </Paragraph>
      </header>
      {gettingStarted && <GettingStartedPanel onboarding={gettingStarted} />}
      {isLoading && <Skeleton variant="rectangle" width="100%" height={320} />}
      {isError && (
        <Alert data-color="danger">
          <Paragraph>{t('hrHome.error')}</Paragraph>
        </Alert>
      )}
      {/* Before the first step the cards would only show zeros, so «Kom i gang» says what comes instead */}
      {data && !beforeFirstStep && (
        <>
          {followUp.length > 0 && (
            <Cards
              id="home-follow-up"
              title={t('hrHome.followUp')}
              cards={followUp}
            />
          )}
          <Cards
            id="home-modules"
            title={t('hrHome.modulesTitle')}
            cards={moduleCards(data, t, i18n.language)}
          />
          <Deadlines data={data} />
        </>
      )}
    </div>
  );
};
